import React, { useState, useEffect } from "react";
import { Modal, Input, Button, Form, Typography, Alert } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { api } from "../../config/api";
import { toast } from "react-toastify";
import { useMyContext } from "../../context/MyContext";
import { useQueryClient } from "@tanstack/react-query";

const { Title, Text } = Typography;

const getInvoiceCurrency = (currency) => {
  return String(currency || "USD").toUpperCase() === "EUR" ? "EUR" : "USD";
};

const formatInvoiceCurrency = (amount, currency) => {
  const numericAmount = Number(amount);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: getInvoiceCurrency(currency),
    minimumFractionDigits: 2,
  }).format(Number.isNaN(numericAmount) ? 0 : numericAmount);
};

const PopUp = ({ visible, onClose, invoiceData }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [newPaidAmount, setNewPaidAmount] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const invoiceCurrency = getInvoiceCurrency(invoiceData?.currency);
  
  // Initialize form and state values when the invoice data changes
  useEffect(() => {
    if (invoiceData) {
      // Use nullish coalescing to handle 0 properly
      const currentRemaining = invoiceData.remaining ?? invoiceData.total ?? 0;
      const previousPaid = invoiceData.paid ?? 0;
      setRemainingAmount(currentRemaining);
      setNewPaidAmount(previousPaid);
      // Reset form and set default value for payment amount
      form.resetFields();
      form.setFieldsValue({ paidAmount: previousPaid });
      // If remaining amount is zero or less, show warning and close
      if (currentRemaining <= 0) {
        toast.warning(`Cannot process partial payment. The remaining amount is ${formatInvoiceCurrency(0, invoiceCurrency)}.`);
        onClose();
      }
    }
  }, [invoiceData, form, onClose, invoiceCurrency]);

  const handlePaidAmountChange = (e) => {
    const payment = parseFloat(e.target.value) || 0;
    setNewPaidAmount(payment);
    // Calculate the new remaining amount
    const total = invoiceData?.total ?? 0;
    const newRemainingAmount = Math.max(0, total - payment);
    setRemainingAmount(newRemainingAmount);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      // Get the payment amount from the form
      const paidAmount = parseFloat(values.paidAmount);
      let newStatus = "Partially Paid";
      if (remainingAmount <= 0) {
        newStatus = "Paid";
      } else if (paidAmount <= 0) {
        newStatus = "Not Paid";
      }

      // Prepare the data to update
      const updateData = {
        paid: paidAmount,
        remaining: remainingAmount.toFixed(2),
        status: newStatus,
        lastPaymentDate: paidAmount > 0 ? new Date().toISOString() : null,
      };
      // Update the invoice with the new payment information
      await api.put(`/invoice/${invoiceData._id}`, updateData);
      toast.success("Payment recorded successfully!");
      queryClient.invalidateQueries(["invoices"]);
      onClose();
    } catch (error) {
      console.error("Error updating payment:", error);
      toast.error("Failed to record payment: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  // Toggle edit mode for payment amount
  const toggleEditPayment = () => {
    setIsEditingPayment(!isEditingPayment);
    if (!isEditingPayment) {
      // When enabling edit mode, make sure the field is editable
      form.setFieldsValue({ paidAmount: newPaidAmount });
    } else {
      // When disabling edit mode, reset to the original value
      form.setFieldsValue({ paidAmount: invoiceData?.paid ?? 0 });
      setNewPaidAmount(invoiceData?.paid ?? 0);
      // Recalculate remaining amount
      const total = invoiceData?.total ?? 0;
      const previousPaid = invoiceData?.paid ?? 0;
      setRemainingAmount(Math.max(0, total - previousPaid));
    }
  };

  // Calculate current remaining for display and validation
  const currentRemaining = invoiceData?.remaining ?? invoiceData?.total ?? 0;
  const total = invoiceData?.total ?? 0;
  
  // If there's no remaining amount, show warning and disable form
  if (currentRemaining <= 0) {
    return (
      <Modal
        title={<Title level={4}>Record Payment</Title>}
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>
        ]}
      >
        <Alert
          message="Cannot Process Payment"
          description={`This invoice has no remaining amount to be paid. The balance is ${formatInvoiceCurrency(0, invoiceCurrency)}.`}
          type="warning"
          showIcon
        />
      </Modal>
    );
  }

  return (
    <Modal
      title={<Title level={4}>Record Payment</Title>}
      open={visible}
      onCancel={onClose}
      footer={null}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div className="mb-4">
          <Text strong>Invoice Number: </Text>
          <Text>{invoiceData?.invoiceNo || "N/A"}</Text>
        </div>
        
        <div className="mb-4">
          <Text strong>Total Invoice Amount: </Text>
          <Text>{formatInvoiceCurrency(total, invoiceCurrency)}</Text>
        </div>
        
        <div className="mb-4">
          <Text strong>Current Outstanding Amount: </Text>
          <Text>{formatInvoiceCurrency(currentRemaining, invoiceCurrency)}</Text>
        </div>
        
        <Form.Item
          label={
            <div className="flex items-center">
              <span>Enter Payment Amount</span>
              <EditOutlined 
                className="ml-2 cursor-pointer text-blue hover:text-blue-700" 
                onClick={toggleEditPayment} 
              />
            </div>
          }
          name="paidAmount"
          rules={[
            { required: true, message: "Please enter the payment amount" },
            {
              validator: (_, value) => {
                const numValue = parseFloat(value);
                if (isNaN(numValue) || numValue < 0) {
                  return Promise.reject("Amount must be greater than or equal to  0");
                }
                // When editing, can change to any amount
                if (isEditingPayment) {
                  if (numValue > total) {
                    return Promise.reject(`Amount cannot exceed total invoice amount ${formatInvoiceCurrency(total, invoiceCurrency)}`);
                  }
                  return Promise.resolve();
                }
                // When not editing, restricted to the previous value
                if (numValue !== (invoiceData?.paid ?? 0)) {
                  return Promise.reject("Click the pencil icon to edit this amount");
                }
                
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input
            type="number"
            step="0.01"
            prefix={invoiceCurrency === "EUR" ? "€" : "$"}
            onChange={handlePaidAmountChange}
            className="w-full"
            readOnly={!isEditingPayment}
          />
        </Form.Item>
        
        <div className="mb-6 p-4 bg-gray-100 rounded-md">
          <Text strong>New Remaining Amount: </Text>
          <Text className="text-lg font-semibold text-blue-700">
            {formatInvoiceCurrency(remainingAmount, invoiceCurrency)}
          </Text>
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            className="bg-blue-600"
          >
            {isEditingPayment ? "Update Payment" : "Record Payment"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default PopUp;
